// The proef edge API (apps/edge, a separate Cloudflare Worker from druif):
// tastings, cellar and hosted/joined flights -- everything that is the
// user's own data, as opposed to api.ts's druif client, which only ever
// reads the shared wine catalogue.
//
// The edge base is a distinct origin from druif's (VITE_EDGE_API_BASE, default
// the same relative "" for local wrangler-dev-behind-a-vite-proxy setups --
// see vite.config.ts). Every call carries X-Device-Id (device.ts); the edge
// Worker's own CORS config (apps/edge/src/app.ts) must list this app's
// origin when the two are NOT proxied together.
import {
  OkResponse,
  AchievementView,
  AddCellarItemRequest,
  CatalogueProposal,
  CellarItem,
  CreateFlightRequest,
  FlightList,
  FlightResults,
  FlightState,
  GamificationSummary,
  JoinFlightRequest,
  LabelScanResponse,
  ListCellarResponse,
  ListTastingsResponse,
  ProposalResponse,
  RevealTastingRequest,
  SaveTastingRequest,
  SubmitFlightNoteRequest,
  Tasting,
  UpdateCellarItemRequest,
  WineIdentity,
  type AddFlightWineRequest,
  WineLook,
  WineLooksResponse,
} from "@proef/contract";
import { z } from "zod";
import { getEdgeBase, isEdgeEnabled } from "./backends";
import { invalidate } from "./dataCache";
import { getDeviceId } from "./device";
import { setIdentity } from "./learn/identity";

export { getEdgeBase };

export class EdgeApiError extends Error {
  readonly status: number;
  /** `ApiError.details` as sent: a refused PUT names its items here (lib/learn/api.ts parses it). */
  readonly details: unknown;
  /** The whole error body as sent, for callers that read more than `error` (the AI quota's 429 carries `resetAt`). */
  readonly body: unknown;
  constructor(status: number, message: string, details?: unknown, body?: unknown) {
    super(message);
    this.name = "EdgeApiError";
    this.status = status;
    this.details = details;
    this.body = body;
  }
}

/**
 * The one choke point for edge calls: every /v1 response carries the analytics
 * identity header, success or error, so the first request already identifies.
 */
export async function call<Schema extends z.ZodTypeAny>(
  schema: Schema,
  path: string,
  init: RequestInit = {},
): Promise<z.infer<Schema>> {
  // Switched off in About (lib/backends.ts): fail before any request leaves the device.
  if (!isEdgeEnabled())
    throw new EdgeApiError(503, "the proef service is switched off in settings");
  const headers = new Headers(init.headers);
  headers.set("x-device-id", getDeviceId());
  if (init.body !== undefined && !headers.has("content-type"))
    headers.set("content-type", "application/json");
  // The edge Worker is a different origin from the PWA in production (Cloudflare Pages vs.
  // workers.dev) -- without "include", the browser never sends better-auth's session cookie
  // cross-site, so a signed-in user looks signed-out on every call after the OAuth redirect.
  const response = await fetch(getEdgeBase() + path, { ...init, headers, credentials: "include" });
  setIdentity(response.headers.get("x-proef-identity"));
  if (response.status === 204) return schema.parse(undefined);
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    let details: unknown;
    let errorBody: unknown;
    try {
      const body: unknown = await response.json();
      errorBody = body;
      if (typeof body === "object" && body !== null) {
        if ("error" in body) {
          const error = (body as Record<string, unknown>)["error"];
          if (typeof error === "string" && error !== "") message = error;
        }
        if ("details" in body) details = (body as Record<string, unknown>)["details"];
      }
    } catch {
      // Non-JSON error body: the status line stands.
    }
    throw new EdgeApiError(response.status, message, details, errorBody);
  }
  return schema.parse(await response.json());
}

/** Runs a write, then forgets the cached reads it made wrong (lib/dataCache.ts), so the next screen fetches instead of painting the old copy. */
async function thenForget<T>(write: Promise<T>, prefixes: readonly string[]): Promise<T> {
  const result = await write;
  await Promise.all(prefixes.map((prefix) => invalidate(prefix)));
  return result;
}
/** A tasting changes the journal and everything the edge derives from it. */
const TASTING_READS = ["user:tastings", "user:gamification", "user:recommendations"] as const;
const CELLAR_READS = ["user:cellar"] as const;

function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}
function put(body: unknown): RequestInit {
  return { method: "PUT", body: JSON.stringify(body) };
}
function patch(body: unknown): RequestInit {
  return { method: "PATCH", body: JSON.stringify(body) };
}

// ---- tastings ----

export function saveTasting(req: z.input<typeof SaveTastingRequest>): Promise<Tasting> {
  return thenForget(
    call(Tasting, "/v1/tastings", post(SaveTastingRequest.parse(req))),
    TASTING_READS,
  );
}
export function listTastings(limit = 50, offset = 0): Promise<ListTastingsResponse> {
  return call(ListTastingsResponse, `/v1/tastings?limit=${String(limit)}&offset=${String(offset)}`);
}
export function getTasting(id: string): Promise<Tasting> {
  return call(Tasting, `/v1/tastings/${encodeURIComponent(id)}`);
}
export function deleteTasting(id: string): Promise<void> {
  return thenForget(
    call(z.void(), `/v1/tastings/${encodeURIComponent(id)}`, { method: "DELETE" }),
    TASTING_READS,
  );
}
export function revealTasting(id: string, req: RevealTastingRequest): Promise<Tasting> {
  return thenForget(
    call(
      Tasting,
      `/v1/tastings/${encodeURIComponent(id)}/reveal`,
      patch(RevealTastingRequest.parse(req)),
    ),
    TASTING_READS,
  );
}

// ---- gamification ----

export function gamificationSummary(): Promise<GamificationSummary> {
  return call(GamificationSummary, "/v1/gamification/summary");
}
export type { AchievementView };

// ---- cellar ----

export function addCellarItem(req: z.input<typeof AddCellarItemRequest>): Promise<CellarItem> {
  return thenForget(
    call(CellarItem, "/v1/cellar", post(AddCellarItemRequest.parse(req))),
    CELLAR_READS,
  );
}
export function listCellar(limit = 50, offset = 0): Promise<ListCellarResponse> {
  return call(ListCellarResponse, `/v1/cellar?limit=${String(limit)}&offset=${String(offset)}`);
}
export function updateCellarItem(
  id: string,
  req: UpdateCellarItemRequest,
): Promise<CellarItem | undefined> {
  return thenForget(
    call(
      CellarItem.optional(),
      `/v1/cellar/${encodeURIComponent(id)}`,
      patch(UpdateCellarItemRequest.parse(req)),
    ),
    CELLAR_READS,
  );
}
export function deleteCellarItem(id: string): Promise<void> {
  return thenForget(
    call(z.void(), `/v1/cellar/${encodeURIComponent(id)}`, { method: "DELETE" }),
    CELLAR_READS,
  );
}

// ---- the user's own wines and catalogue proposals ----

/** Stores a wine the user entered or confirmed, so a tasting or the cellar can point at it without druif. */
export function saveWine(wine: WineIdentity): Promise<WineIdentity> {
  return call(WineIdentity, "/v1/wines", post(WineIdentity.parse(wine)));
}
/** A wine as the edge holds it, for the page of a wine no catalogue has. */
export function getEdgeWine(id: string): Promise<WineIdentity> {
  return call(WineIdentity, `/v1/wines/${encodeURIComponent(id)}`);
}
/** The colours this device judged the given wines to be (absent for a wine it never judged). */
export function getWineLooks(ids: readonly string[]): Promise<WineLooksResponse> {
  return call(
    WineLooksResponse,
    `/v1/wine-looks?ids=${ids.map((id) => encodeURIComponent(id)).join(",")}`,
  );
}
/** Keeps the colour just judged for a wine, as the pre-selection of its next tasting. */
export function putWineLook(wineId: string, look: WineLook): Promise<void> {
  return call(z.void(), `/v1/wine-looks/${encodeURIComponent(wineId)}`, put(WineLook.parse(look)));
}
/** An explicit opt-in suggestion for the shared catalogue; wine and e-label facts only. */
export function proposeWine(
  proposal: z.input<typeof CatalogueProposal>,
): Promise<ProposalResponse> {
  return call(ProposalResponse, "/v1/proposals", post(CatalogueProposal.parse(proposal)));
}

// ---- label scan (Workers AI on the edge, quota-limited) ----

/** The photo as the raw body: the edge reads it with a vision model and answers what it saw plus what is left of today's allowance. */
export function scanLabel(photo: Blob): Promise<LabelScanResponse> {
  return call(LabelScanResponse, "/v1/scan/label", {
    method: "POST",
    body: photo,
    headers: { "content-type": photo.type === "" ? "image/jpeg" : photo.type },
  });
}

// ---- flights ----

export function createFlight(req: z.input<typeof CreateFlightRequest>): Promise<FlightState> {
  return call(FlightState, "/v1/flights", post(CreateFlightRequest.parse(req)));
}
export function getFlight(code: string): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}`);
}
export function joinFlight(code: string, req: JoinFlightRequest): Promise<FlightState> {
  return call(
    FlightState,
    `/v1/flights/${encodeURIComponent(code)}/join`,
    post(JoinFlightRequest.parse(req)),
  );
}
export function addFlightWine(code: string, req: AddFlightWineRequest): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}/wines`, post(req));
}
export function startFlight(code: string): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}/start`, { method: "POST" });
}
export function revealFlightWine(code: string): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}/reveal`, { method: "POST" });
}
export function nextFlightWine(code: string): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}/next`, { method: "POST" });
}
/** A flight note is a tasting too (the edge joins flight_notes into the journal and streaks). */
export function submitFlightNote(code: string, req: SubmitFlightNoteRequest): Promise<FlightState> {
  return thenForget(
    call(
      FlightState,
      `/v1/flights/${encodeURIComponent(code)}/notes`,
      post(SubmitFlightNoteRequest.parse(req)),
    ),
    TASTING_READS,
  );
}
/** The flights this device, or the signed-in account on any device, is in and that have not finished. */
export function listMyFlights(): Promise<FlightList> {
  return call(FlightList, "/v1/flights?mine=1");
}
/** The host closes a flight outside any session: it is done for everyone. */
export function endFlight(code: string): Promise<FlightState> {
  return call(FlightState, `/v1/flights/${encodeURIComponent(code)}/end`, { method: "POST" });
}
/** A guest steps out of a flight outside any session; their notes stay and it leaves their lists. */
export async function leaveFlight(code: string): Promise<void> {
  await call(OkResponse, `/v1/flights/${encodeURIComponent(code)}/leave`, { method: "POST" });
}
export function getFlightResults(code: string): Promise<FlightResults> {
  return call(FlightResults, `/v1/flights/${encodeURIComponent(code)}/results`);
}

export type {
  AddCellarItemRequest,
  CellarItem,
  CreateFlightRequest,
  FlightResults,
  FlightState,
  GamificationSummary,
  JoinFlightRequest,
  ListCellarResponse,
  ListTastingsResponse,
  RevealTastingRequest,
  SaveTastingRequest,
  SubmitFlightNoteRequest,
  Tasting,
  UpdateCellarItemRequest,
};
